// Session revocation + logout coordination — PURE decision helpers.
// Zero DOM, zero supabase, zero next imports: unit-tested in isolation.
//
// Wiring: /api/heartbeat (server re-sign), session-client.ts (browser kick +
// logout ordering), DashboardLayout / DashboardRail / data-context (callers).
//
// Why this exists (2026-08-15): the heartbeat (#777) refuses to renew a
// DEACTIVATED staff member's session and answers 401 — but the client ping
// discarded the response, so the still-valid cookie kept working for its
// full 12h. Deactivation is the clinic's only revocation lever, and it did
// nothing to an open tab. Same class: the clinical `role` claim ('MD' gates
// the Comfy review queue via isComfyReviewer) rode the sliding re-sign
// unchanged forever, exactly the frozen-at-login hole #780 closed for
// system_role.

/** Claims the DATABASE holds right now for the heartbeating staff row.
 *  `undefined`/null/'' = "no fresh value" (DB blip, or column blank) — keep
 *  the claim already in the cookie rather than lock anyone out. */
export type LiveStaffClaims = {
  systemRole?: string | null;
  role?: string | null;
};

/** Overlay the live DB claims onto the verified session payload before the
 *  sliding re-sign. requireRole reads `system_role` from the cookie and
 *  isComfyReviewer reads `role`; both would slide forward frozen-at-login on
 *  an always-open tab (a demoted admin keeps admin; a doctor re-rostered as
 *  NP keeps the review queue). Returns the SAME object when nothing changed
 *  so callers can cheaply tell "re-signed with a change" apart. */
export function applyLiveStaffClaims<T extends { system_role?: string; role?: string }>(
  session: T,
  live: LiveStaffClaims,
): T {
  let out = session;
  if (live.systemRole && live.systemRole !== session.system_role) {
    out = { ...out, system_role: live.systemRole };
  }
  if (live.role && live.role !== session.role) {
    out = { ...out, role: live.role };
  }
  return out;
}

/** The only response status that means "this session is no longer accepted":
 *  the cookie is missing/expired/invalid (proxy or requireRole) OR the staff
 *  row is deactivated (heartbeat revocation gate). 400 ("Sesión incompleta"),
 *  403 and 5xx are NOT session-gone. */
export function isSessionGoneStatus(status: number): boolean {
  return status === 401;
}

/** Where a kicked tab goes. The login page lives at '/' — '/login' is a
 *  legacy PUBLIC_PATHS entry with NO page (404; incident #775). Mirrors
 *  proxy.ts buildLoginRedirect: '/' + ?next=<path+search> so staff lands
 *  back where they were after signing in. Returns null when ALREADY on '/':
 *  there a 401 is the logged-out steady state and redirecting causes the
 *  reload loop of incident #776. */
export function loginKickHref(pathname: string, search: string): string | null {
  if (!pathname || pathname === '/') return null;
  return `/?next=${encodeURIComponent(pathname + (search || ''))}`;
}

/** Loop breaker. A kick clears cookies twice (server Set-Cookie on the 401
 *  + client DELETE /api/auth) so the proxy at '/' will show the login page.
 *  If BOTH somehow fail, '/' would bounce a still-valid cookie back to /home,
 *  the next heartbeat would 401, and we would loop at page-load speed. A
 *  second kick inside this window is suppressed (and reported) instead. */
export const KICK_LOOP_WINDOW_MS = 15_000;

export function kickSuppressed(lastKickAtMs: number | null | undefined, nowMs: number): boolean {
  if (lastKickAtMs === null || lastKickAtMs === undefined || !Number.isFinite(lastKickAtMs)) return false;
  const elapsed = nowMs - lastKickAtMs;
  return elapsed >= 0 && elapsed < KICK_LOOP_WINDOW_MS;
}

/** Logout must not race the heartbeat: an in-flight heartbeat RESPONSE
 *  Set-Cookies a fresh session + sb_jwt, so if it lands after DELETE
 *  /api/auth cleared them the front-desk iPad is silently logged back in.
 *  Logout therefore waits for the in-flight ping first — but bounded, so a
 *  hung request can never make the logout button feel dead. */
export const LOGOUT_AWAIT_HEARTBEAT_MS = 4_000;

/** The client-side DELETE /api/auth that follows a kick is belt-and-braces
 *  (the heartbeat 401 already cleared both cookies; a proxy 401 leaves only a
 *  possibly-stale sb_jwt). It must never HOLD the navigation hostage: on a
 *  flaky network fetch() has no timeout, and a kicked tab that sits frozen is
 *  worse than one that reaches the login page with a stale sb_jwt (which the
 *  proxy now clears on the next redirect anyway). Bounded, then navigate.
 *  On timeout the request is ABORTED (and deliberately no `keepalive`): a
 *  DELETE that outlives the navigation could land AFTER a fast re-login on
 *  the same tab and wipe the fresh cookies. */
export const KICK_DELETE_TIMEOUT_MS = 2_500;

/** Longer bound for the explicit logout DELETE — there it is THE cookie
 *  clear (nothing cleared them server-side beforehand), so it earns more
 *  patience than the kick's belt-and-braces call. Still bounded: with
 *  cookies still valid the proxy bounces '/' → /home, the honest "logout
 *  did not stick" signal, and far better than a button that appears to do
 *  nothing forever. */
export const LOGOUT_DELETE_TIMEOUT_MS = 4_000;

/** Zero-cost revocation check for /api/data: that route already loads every
 *  staff row of the clinic (getStaff) to build its payload, so the caller's
 *  own row is in hand — no extra round trip. TRUE only when the row is
 *  present AND active === false. A missing row / null active is NOT revoked
 *  (fail-open; the heartbeat's UPDATE…RETURNING gate covers hard deletes),
 *  because this runs on the boot-critical route and must never lock out a
 *  legit session on a data hiccup. */
export function sessionStaffRevoked(
  staff: ReadonlyArray<{ id: string; active?: boolean | null }>,
  sessionStaffId: string | null | undefined,
): boolean {
  if (!sessionStaffId) return false;
  const me = staff.find((s) => s.id === sessionStaffId);
  return !!me && me.active === false;
}
